import React, { useState, useMemo } from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { duration } from './primitives/tokens';
import { Spin } from './Spin';
import { Empty } from './Empty';
import { Pagination } from './Pagination';
import { Icon } from './Icon';
import styles from './Table.module.css';

/** CSS class for hover-revealed action buttons in a table row's last column.
 *  Apply to a wrapper div inside a column's `render` function — it will be
 *  hidden by default and fade in when the row is hovered.
 *
 *  @example
 *  { title: '', render: (_, record) => (
 *    <div className={tableActionsCell}>
 *      <IconButton onClick={() => onDelete(record)}><Icon name="delete" /></IconButton>
 *    </div>
 *  )}
 */
export const tableActionsCell = styles.actionsCell;

type SortOrder = 'ascend' | 'descend' | null;

interface TableColumn<T> {
  title: React.ReactNode;
  dataIndex?: string;
  key?: string;
  render?: (value: unknown, record: T, index: number) => React.ReactNode;
  sorter?: (a: T, b: T) => number;
  width?: number | string;
  ellipsis?: boolean;
  fixed?: 'left' | 'right';
  align?: 'left' | 'center' | 'right';
}

interface TableProps<T> extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  dataSource: T[];
  columns: TableColumn<T>[];
  rowKey?: string | ((record: T) => string);
  pagination?: false | { pageSize?: number; current?: number; total?: number; onChange?: (page: number) => void };
  loading?: boolean;
  size?: 'default' | 'small' | 'large';
  scroll?: { x?: number | string; y?: number | string };
  expandable?: { expandedRowRender: (record: T) => React.ReactNode; rowExpandable?: (record: T) => boolean };
  rowSelection?: { selectedRowKeys: string[]; onChange: (keys: string[]) => void };
  summary?: () => React.ReactNode;
  onRow?: (record: T, index: number) => React.HTMLAttributes<HTMLTableRowElement>;
}

/**
 * Table — tabular data display with sorting and pagination.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | dataSource | T[] | — | Row data |
 * | columns | TableColumn[] | — | Column definitions |
 * | rowKey | string \| (record) => string | — | Unique row key |
 * | pagination | false \| PaginationConfig | default | Pagination config |
 * | loading | boolean | false | Loading state |
 * | size | 'default' \| 'small' | 'default' | Cell padding |
 * | scroll | { x?, y? } | — | Scrollable area |
 * | expandable | object | — | Expandable rows |
 * | rowSelection | object | — | Checkbox selection |
 * | summary | () => ReactNode | — | Summary row |
 *
 * @example
 * <Table
 *   dataSource={data}
 *   columns={[
 *     { title: 'Name', dataIndex: 'name', sorter: (a, b) => a.name.localeCompare(b.name) },
 *     { title: 'Age', dataIndex: 'age' },
 *   ]}
 * />
 */
export function Table<T extends Record<string, unknown>>({
  dataSource,
  columns,
  rowKey,
  pagination: paginationProp,
  loading = false,
  size = 'default',
  scroll,
  expandable,
  rowSelection,
  summary,
  onRow,
  className,
  style,
  ...props
}: TableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [internalPage, setInternalPage] = useState(1);

  const getKey = (record: T, index: number): string => {
    if (typeof rowKey === 'function') return rowKey(record);
    if (typeof rowKey === 'string') return String(record[rowKey]);
    return String(record.key ?? record.id ?? index);
  };

  const handleSort = (col: TableColumn<T>) => {
    const key = col.key ?? col.dataIndex ?? '';
    if (sortKey === key) {
      setSortOrder(sortOrder === 'ascend' ? 'descend' : sortOrder === 'descend' ? null : 'ascend');
      if (sortOrder === 'descend') setSortKey(null);
    } else {
      setSortKey(key);
      setSortOrder('ascend');
    }
  };

  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) return dataSource;
    const col = columns.find((c) => (c.key ?? c.dataIndex) === sortKey);
    if (!col?.sorter) return dataSource;
    const sorted = [...dataSource].sort(col.sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [dataSource, columns, sortKey, sortOrder]);

  // Pagination
  const hasPagination = paginationProp !== false;
  const pageSize = (typeof paginationProp === 'object' ? paginationProp.pageSize : undefined) ?? 10;
  const currentPage = (typeof paginationProp === 'object' ? paginationProp.current : undefined) ?? internalPage;
  const total = (typeof paginationProp === 'object' ? paginationProp.total : undefined) ?? sortedData.length;
  const onPageChange = (typeof paginationProp === 'object' ? paginationProp.onChange : undefined) ?? setInternalPage;

  const paginatedData = hasPagination
    ? sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : sortedData;

  const sizeThClass = size === 'small' ? styles.thSmall : size === 'large' ? styles.thLarge : styles.thDefault;
  const sizeTdClass = size === 'small' ? styles.tdSmall : size === 'large' ? styles.tdLarge : styles.tdDefault;

  // Table width from scroll.x — CSS custom property
  const tableStyle = scroll?.x
    ? ({ '--table-width': typeof scroll.x === 'number' ? `${scroll.x}px` : scroll.x } as React.CSSProperties)
    : undefined;

  // Scroll container max-height from scroll.y — CSS custom property
  const scrollWrapperStyle = scroll?.y
    ? ({ '--table-max-height': typeof scroll.y === 'number' ? `${scroll.y}px` : scroll.y } as React.CSSProperties)
    : undefined;

  const isSelecting = !!(rowSelection && rowSelection.selectedRowKeys.length > 0);

  return (
    <div className={clsx(styles.root, className)} style={style} {...props}>
      <Spin spinning={loading}>
        <div
          className={clsx(
            styles.scrollWrapper,
            scroll?.x && styles.scrollX,
            scroll?.y && styles.scrollY,
          )}
          style={scrollWrapperStyle}
        >
          <table className={styles.table} style={tableStyle}>
            <thead>
              <tr>
                {expandable && (
                  <th className={clsx(styles.th, sizeThClass, styles.cellNarrow)} />
                )}
                {columns.map((col, ci) => {
                  const key = col.key ?? col.dataIndex ?? String(ci);
                  const isSorted = sortKey === key;
                  const colWidthStyle = col.width
                    ? ({ width: col.width } as React.CSSProperties)
                    : undefined;
                  const alignClass = col.align === 'center'
                    ? styles.alignCenter
                    : col.align === 'right'
                      ? styles.alignRight
                      : undefined;
                  return (
                    <th
                      key={key}
                      className={clsx(styles.th, sizeThClass, alignClass, col.sorter && styles.thSortable)}
                      style={colWidthStyle}
                      onClick={() => col.sorter && handleSort(col)}
                      aria-sort={
                        col.sorter
                          ? isSorted
                            ? sortOrder === 'ascend'
                              ? 'ascending'
                              : 'descending'
                            : 'none'
                          : undefined
                      }
                      onKeyDown={(e) => {
                        if (col.sorter && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleSort(col);
                        }
                      }}
                      tabIndex={col.sorter ? 0 : undefined}
                    >
                      <span className={styles.headerTitle}>
                        {col.title}
                        {col.sorter && (
                          <span className={clsx(styles.sortIndicator, isSorted && styles.sortIndicatorActive)}>
                            <Icon
                              name="expand_less"
                              size={10}
                              className={clsx(styles.sortUp, isSorted && sortOrder === 'ascend' && styles.sortUpActive)}
                            />
                            <Icon
                              name="expand_more"
                              size={10}
                              className={clsx(styles.sortDown, isSorted && sortOrder === 'descend' && styles.sortDownActive)}
                            />
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length + (expandable ? 1 : 0)}
                    className={clsx(styles.td, sizeTdClass)}
                  >
                    <Empty description="No data" />
                  </td>
                </tr>
              ) : (
                paginatedData.map((record, ri) => {
                  const key = getKey(record, ri);
                  const isExpanded = expandedKeys.has(key);
                  const isExpandable =
                    expandable && (expandable.rowExpandable ? expandable.rowExpandable(record) : true);
                  const isSelected = rowSelection?.selectedRowKeys.includes(key);

                  const baseRowProps = onRow ? onRow(record, ri) : {};
                  const rowProps = isSelecting
                    ? { ...baseRowProps, onClick: undefined }
                    : baseRowProps;
                  return (
                    <React.Fragment key={key}>
                      <tr
                        className={clsx(styles.rowHover, isSelected && styles.rowSelected, !isSelecting && onRow && styles.rowClickable)}
                        {...rowProps}
                      >
                        {expandable && (
                          <td
                            className={clsx(styles.td, sizeTdClass, styles.cellNarrow, isExpandable ? styles.thSortable : undefined)}
                            role={isExpandable ? 'button' : undefined}
                            tabIndex={isExpandable ? 0 : undefined}
                            aria-expanded={isExpandable ? isExpanded : undefined}
                            aria-label={isExpandable ? 'Expand row' : undefined}
                            onClick={() =>
                              isExpandable &&
                              setExpandedKeys((prev) => {
                                const next = new Set(prev);
                                next.has(key) ? next.delete(key) : next.add(key);
                                return next;
                              })
                            }
                            onKeyDown={(e) => {
                              if (isExpandable && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                setExpandedKeys((prev) => {
                                  const next = new Set(prev);
                                  next.has(key) ? next.delete(key) : next.add(key);
                                  return next;
                                });
                              }
                            }}
                          >
                            {isExpandable && (
                              <motion.span
                                animate={{ rotate: isExpanded ? 90 : 0 }}
                                transition={{ duration: duration.normal / 1000 }}
                                className={styles.expandIcon}
                              >
                                <Icon name="chevron_right" size={14} />
                              </motion.span>
                            )}
                          </td>
                        )}
                        {columns.map((col, ci) => {
                          const colKey = col.key ?? col.dataIndex ?? String(ci);
                          const value = col.dataIndex ? record[col.dataIndex] : undefined;
                          const alignClass = col.align === 'center'
                            ? styles.alignCenter
                            : col.align === 'right'
                              ? styles.alignRight
                              : undefined;
                          const colCellStyle = col.width
                            ? ({ width: col.width, minWidth: col.width } as React.CSSProperties)
                            : undefined;
                          const cellContent = col.render ? col.render(value, record, ri) : (value as React.ReactNode);
                          return (
                            <td
                              key={colKey}
                              className={clsx(
                                styles.td,
                                styles.tdClip,
                                sizeTdClass,
                                alignClass,
                                col.ellipsis && styles.ellipsis,
                              )}
                              style={colCellStyle}
                            >
                              {cellContent}
                            </td>
                          );
                        })}
                      </tr>
                      {isExpanded && expandable && (
                        <tr>
                          <td
                            colSpan={columns.length + 1}
                            className={clsx(styles.expandedRow, sizeTdClass)}
                          >
                            {expandable.expandedRowRender(record)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
            {summary && (
              <tfoot>
                <tr>
                  <td
                    colSpan={columns.length + (expandable ? 1 : 0)}
                    className={clsx(styles.summaryCell, sizeTdClass)}
                  >
                    {summary()}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Spin>

      {hasPagination && total > pageSize && (
        <div className={styles.pagination}>
          <Pagination total={total} pageSize={pageSize} current={currentPage} onChange={onPageChange} />
        </div>
      )}
    </div>
  );
}
